import PrimaryInnerGlowButton from '@/components/buttons/primary';
import FrontPinkButton from '@/components/buttons/primary/FrontPinkButton';
import { SecondaryDefaultButton } from '@/components/buttons/secondary';
import Carousel from '@/components/carousels/dots';
import Image from '@/components/Image';
import { Container } from '@/components/layout/Container';
import Link from '@/components/Link';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import { INTEGRATIONS_HOME, PRICING_PAGE } from '@/constants/routes';
import { GlobalContext } from '@/context';
import { IntegrationPathsQuery, IntegrationQuery, IntegrationQueryVariables } from '@/graphql';
import IntegrationQueryDocument from '@/graphql/queries/Integration.gql';
import IntegrationPathsQueryDocument from '@/graphql/queries/IntegrationPaths.gql';
import ResourceLayout from '@/layouts/ResourceLayout';
import { getPageProps } from '@/lib/craft-page';
import { createLogger } from '@/lib/debug';
import { fetchRelatedLinks } from '@/lib/graphite/fetch-related-links';
import { GraphiteRelatedLink } from '@/lib/graphite/types';
import CraftClient from '@/lib/graphql';
import gtm from '@/lib/gtm';
import useBreakpoint from '@/lib/hooks/useBreakpoint';
import { getStaticPagePaths, getStaticPageProps } from '@/lib/next';
import { getSingle, maybeNotSet, notEmpty } from '@/lib/typescript-helpers';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import {
  applyBreakpointNegativeMargins,
  marginY,
  mediaBreakpointDown,
  mediaBreakpointUp,
  paddingX,
} from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import MasterTemplate from '@/templates/MasterTemplate';
import { Parser as HTMLParser } from 'html-to-react';
import { useContext, useMemo } from 'react';
import styled from 'styled-components';
// #region - Components

const HeroHeadline = styled.h1`
  ${customTextSize(40, 42)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(56, 53)}
    font-weight: ${FONT_WEIGHTS.MEDIUM};
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  grid-template-rows: auto auto;
  grid-column-gap: ${remCalc(16)};
  grid-row-gap: ${remCalc(16)};
  align-items: center;

  ${mediaBreakpointUp('desktop')} {
    grid-column-gap: ${remCalc(24)};
    grid-row-gap: ${remCalc(8)};
  }

  div {
    /* Category */
    &:nth-child(1) {
      grid-row: 1;
      grid-column: 1 / 3;
    }

    /* Logo */
    &:nth-child(2) {
      grid-row: 2;
      grid-column: 1;
    }

    /* Title */
    &:nth-child(3) {
      grid-row: 2;
      grid-column: 2;
    }

    ${mediaBreakpointUp('desktop')} {
      /* Category */
      &:nth-child(1) {
        grid-row: 1;
        grid-column: 2;
      }

      /* Logo */
      &:nth-child(2) {
        grid-row: 1 / 3;
        grid-column: 1;
      }

      /* Title */
      &:nth-child(3) {
        grid-row: 2;
        grid-column: 2;
      }
    }
  }
`;

const CategoryLabel = styled.div`
  display: inline-block;
  background-color: ${hexToRGBA(COLORS.GRAY.LOWLIGHT, 0.1)};
  ${customTextSize(14, 16)}
  padding: ${remCalc(4, 8)};
  font-weight: ${FONT_WEIGHTS.BOLD};
  border-radius: ${remCalc(8)};
`;

const IntegrationLogo = styled(Image)`
  border-radius: 100%;
  overflow: hidden;
  box-shadow: rgb(0 27 56 / 16%) 0px 2px 8px;
  width: ${remCalc(56)};
  height: ${remCalc(56)};

  ${mediaBreakpointUp('desktop')} {
    width: ${remCalc(80)};
    height: ${remCalc(80)};
  }
`;

const MainContent = styled.section`
  display: grid;
  grid-template-columns: 100%;
  grid-row-gap: ${remCalc(36)};

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: auto ${remCalc(360)};
    grid-column-gap: ${remCalc(64)};
  }
`;

const Content = styled.div`
  ${customTextSize(16, 26)}

  a {
    color: ${COLORS.BLUE.LOWLIGHT};
  }

  h2 {
    ${customTextSize(24, 30)}
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    margin-bottom: ${remCalc(20)};
  }

  p + p {
    margin-top: ${remCalc(20)};
  }

  ul,
  ol {
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    padding-inline-start: 20px;

    li + li {
      margin-top: 10px;
    }
  }

  ul {
    list-style-type: disc;

    ul {
      list-style-type: circle;
    }
  }

  ol {
    list-style-type: decimal;

    ol {
      list-style-type: lower-alpha;

      ol {
        list-style-type: lower-roman;
      }
    }
  }

  ${mediaBreakpointUp('desktop')} {
    h2 {
      ${customTextSize(40, 42)}
      margin-bottom: ${remCalc(32)};
    }
  }
`;

const Screenshot = styled(Image)`
  border-radius: ${remCalc(10)};
  border: 2px solid ${COLORS.GRAY.HIGHLIGHT};
  margin-top: ${remCalc(40)};
  width: 100%;
`;

const CompanyInfoBlock = styled.div`
  background-color: ${COLORS.GRAY.HIGHLIGHT};
  border-radius: ${remCalc(10)};

  ${mediaBreakpointUp('desktop')} {
    margin: 0 0 0 auto;
    width: ${remCalc(290)};
    position: sticky;
    top: ${remCalc(PRIMARY_NAV_HEIGHT + 40)};
  }
`;

const IntegrationLogoTitle = styled.div`
  padding: ${remCalc(16, 32)};
  border-bottom: 2px solid ${COLORS.WHITE};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  ${customTextSize(24, 30)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};

  ${IntegrationLogo} {
    width: ${remCalc(32)};
    height: ${remCalc(32)};
    margin-right: ${remCalc(12)};
    flex-shrink: 0;
  }
`;

const CompanyContent = styled.div`
  padding: ${remCalc(16, 32, 32)};

  ${SecondaryDefaultButton} {
    margin-bottom: ${remCalc(16)};
  }
`;

const CompanyDetails = styled.ul`
  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(16)};
  margin-bottom: ${remCalc(32)};
`;

const CompanyDetailItem = styled.li`
  h6 {
    font-weight: ${FONT_WEIGHTS.BOLD};
    ${customTextSize(16, 26)}
  }

  p {
    ${customTextSize(14, 24)}
  }

  a {
    color: ${COLORS.BLUE.LOWLIGHT};
    font-weight: ${FONT_WEIGHTS.BOLD};
  }

  &.resources-links {
    a {
      display: block;
      text-decoration: none;

      & + a {
        margin-top: ${remCalc(2)};
      }
    }
  }
`;

const CTABlock = styled.div`
  border-radius: ${remCalc(10)};
  background: radial-gradient(
      41.32% 159.33% at 6.39% 9.17%,
      #ffbfda 17.53%,
      rgba(255, 255, 255, 0) 100%,
      rgba(255, 255, 255, 0) 100%
    ),
    radial-gradient(43.45% 208.5% at 47.99% 116.5%, #ffecb3 13.88%, rgba(255, 255, 255, 0) 100%),
    radial-gradient(92.78% 619.69% at 103.82% 93%, #b0f1fc 0%, rgba(255, 236, 179, 0) 100%), #ffffff;
  padding: ${remCalc(32)};
  ${marginY(40)};

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  row-gap: ${remCalc(40)};

  ${mediaBreakpointUp('desktop')} {
    flex-direction: row;
    justify-content: space-between;
    padding: ${remCalc(42, 104)};
    min-height: ${remCalc(168)};
  }

  p {
    ${customTextSize(40, 42)};
    color: ${COLORS.BLACK};
    text-align: center;
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};

    ${mediaBreakpointUp('desktop')} {
      text-align: left;
    }
  }

  div {
    display: flex;
    flex-direction: column;
    gap: ${remCalc(16)};

    ${mediaBreakpointUp('desktop')} {
      flex-direction: row;
    }
  }
`;

const RelatedSection = styled.div`
  margin-bottom: ${remCalc(80)};
`;

const RelatedSectionHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-bottom: ${remCalc(24)};

  ${mediaBreakpointUp('desktop')} {
    justify-content: space-between;
    border-top: 1px solid ${hexToRGBA(COLORS.GRAY.BASE, 0.5)};
    padding-top: ${remCalc(40)};
  }

  h4 {
    ${customTextSize(40, 42)}
  }

  a {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    color: ${COLORS.BLACK};

    ${mediaBreakpointDown('tablet')} {
      display: none;
    }
  }
`;

const RelatedSectionFooter = styled.div`
  text-align: center;
  margin-top: ${remCalc(16)};

  ${mediaBreakpointUp('desktop')} {
    display: none;
  }

  a {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    color: ${COLORS.BLUE.LOWLIGHT};
  }
`;

const StyledCarousel = styled(Carousel)`
  ${applyBreakpointNegativeMargins}
`;

const RelatedList = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  column-gap: ${remCalc(16)};
  row-gap: ${remCalc(16)};
`;

const RelatedItem = styled.div`
  background-color: ${COLORS.WHITE};
  border-radius: ${remCalc(10)};
  box-shadow: rgb(0 27 56 / 10%) 0px 0px 30px;
  transition: background-color 0.2s ease-in-out;

  ${mediaBreakpointUp('desktop')} {
    grid-column: span 1;
  }

  &:hover {
    background-color: #F3F3F4; /* gray-100 */
  }

  a {
    appearance: none;
    width: 100%;
    height: 100%;
    min-height: ${remCalc(220)};
    ${paddingX(16)}

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  h2 {
    ${customTextSize(24, 30)};
    font-weight: ${FONT_WEIGHTS.MEDIUM};
    margin-bottom: ${remCalc(8)};
    text-align: center;

    small {
      display: block;
      ${customTextSize(18, 30)};
    }
  }

  p {
    ${customTextSize(14, 16)};
  }
`;

const RelatedItemLogo = styled.div`
  width: ${remCalc(64)};
  height: ${remCalc(64)};
  border-radius: 100%;
  overflow: hidden;
  box-shadow: rgb(0 27 56 / 16%) 0px 4px 16px;
  margin-bottom: ${remCalc(20)};
`;

// #endregion - Components

const htmlToReactParser = new HTMLParser();

const Integration: PageTemplate<IntegrationQuery & { graphiteRelatedLinks?: GraphiteRelatedLink[] }> = ({ entry, graphiteRelatedLinks }) => {
  const [, activeBreakpoints] = useBreakpoint();
  const { toggleGetStartedModal } = useContext(GlobalContext);

  const logo = useMemo(() => {
    const logo = getSingle(entry?.logo);

    if (!logo) return null;

    return <IntegrationLogo property="graphite:image" src={logo.url!} width={80} height={80} alt={logo.altText ?? ''} />;
  }, [entry]);

  const relatedIntegrations = useMemo(
    () =>
      graphiteRelatedLinks
        ?.map((integration, index) => {
          if (!integration) return null;

          const logoSrc = integration?.image_url;
          const category = getSingle(integration?.category);

          if (!logoSrc || !category) return null;

          return (
            <RelatedItem key={integration.title}>
              <Link href={integration.url ?? '#'}>
                <a
                  onClick={() => {
                    gtm.track('click', {
                      element_type: 'link',
                      element_label: `Position ${index} | ${integration.title}`,
                      location: 'Other Integrations Section',
                    });
                  }}
                >
                  <RelatedItemLogo>
                    <Image src={logoSrc} width={64} height={64} alt="" />
                  </RelatedItemLogo>

                  <h2>{integration?.title}</h2>
                  <p>{category}</p>
                </a>
              </Link>
            </RelatedItem>
          );
        })
        .filter(notEmpty) ?? [],
    [graphiteRelatedLinks],
  );

  return (
    <>
      <ResourceLayout
        color={entry?.color && hexToRGBA(entry?.color, 0.15)}
        backButton={{
          href: INTEGRATIONS_HOME,
          onClick: () => {
            gtm.track('click', {
              element_type: 'link',
              element_label: 'Back to Integrations Home',
              location: 'Integration Header',
            });
          },
        }}
        headerLeftColumn={
          <HeroGrid>
            <div>
              <CategoryLabel property="graphite:category">{getSingle(entry?.category)?.title}</CategoryLabel>
            </div>
            <div>{logo}</div>
            <div>
              <HeroHeadline>{entry?.title?.replace(/-/g, '\u2011')}</HeroHeadline>
            </div>
          </HeroGrid>
        }
      >
        <MainContent>
          <article>
            <Content>
              <h2>{entry?.summary}</h2>
              {entry?.description && htmlToReactParser.parse(entry?.description)}
            </Content>

            {entry?.screenshots?.map((screenshot, index) => {
              if (!screenshot?.url) return null;

              return (
                <Screenshot
                  key={screenshot.id}
                  src={screenshot?.url}
                  width={screenshot?.width!}
                  height={screenshot?.height!}
                  alt={`Screenshot of ${entry.title}`}
                />
              );
            })}
          </article>

          <aside>
            <CompanyInfoBlock>
              <IntegrationLogoTitle>
                {logo} <span>{entry?.title?.replace(/-/g, '\u2011')}</span>
              </IntegrationLogoTitle>

              <CompanyContent>
                <CompanyDetails>
                  {!!entry?.plans && (
                    <CompanyDetailItem>
                      <h6>Available on</h6>
                      <p>
                        {entry.plans.map((plan, index) => (
                          <>
                            {index > 0 && <span>, </span>}
                            <Link href={PRICING_PAGE}>
                              <a
                                onClick={() => {
                                  gtm.track('click', {
                                    element_type: 'link',
                                    element_label: 'Pricing Plan Link',
                                    plan_name: maybeNotSet(plan?.title),
                                  });
                                }}
                              >
                                {plan?.title}
                              </a>
                            </Link>
                          </>
                        ))}
                      </p>
                    </CompanyDetailItem>
                  )}

                  {!!entry?.publisher && (
                    <CompanyDetailItem>
                      <h6>Publisher</h6>
                      <p>{entry?.publisher}</p>
                    </CompanyDetailItem>
                  )}

                  {(!!entry?.website || !!entry?.privacyPolicy) && (
                    <CompanyDetailItem className="resources-links">
                      <h6>Partner resources</h6>
                      <p>
                        {!!entry?.website && (
                          <a
                            href={entry?.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              gtm.track('click', {
                                element_type: 'link',
                                element_label: 'Partner Website',
                                location: 'Integration Info Block',
                              });
                            }}
                          >
                            Website
                          </a>
                        )}
                        {!!entry?.privacyPolicy && (
                          <a
                            href={entry?.privacyPolicy}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              gtm.track('click', {
                                element_type: 'link',
                                element_label: 'Partner Privacy Policy',
                                location: 'Integration Info Block',
                              });
                            }}
                          >
                            Privacy policy
                          </a>
                        )}
                      </p>
                    </CompanyDetailItem>
                  )}
                </CompanyDetails>

                {!!entry?.support && (
                  <SecondaryDefaultButton
                    as="a"
                    href={entry?.support}
                    target="_blank"
                    onClick={() => {
                      gtm.track('click', {
                        element_type: 'link',
                        element_label: 'View setup guide',
                        location: 'Integration Info Block',
                      });
                    }}
                    fullWidth
                  >
                    View setup guide
                  </SecondaryDefaultButton>
                )}

                <PrimaryInnerGlowButton
                  onClick={() => {
                    toggleGetStartedModal(true);

                    gtm.track('click', {
                      element_type: 'button',
                      element_label: 'Get Started',
                      location: 'Integration Info Block',
                    });
                  }}
                  fullWidth
                >
                  Get started
                </PrimaryInnerGlowButton>
              </CompanyContent>
            </CompanyInfoBlock>
          </aside>
        </MainContent>
      </ResourceLayout>


      <Container>
        {relatedIntegrations.length > 0 && (
          <RelatedSection>
            <RelatedSectionHeader>
              <h4>You might also like</h4>

              <Link href={INTEGRATIONS_HOME}>
                <a
                  onClick={() => {
                    gtm.track('click', {
                      element_label: 'See all integrations',
                      element_type: 'link',
                      location: 'Other Integrations Section',
                    });
                  }}
                >
                  See all integrations
                </a>
              </Link>
            </RelatedSectionHeader>

            {!activeBreakpoints.includes('desktop') ? (
              <StyledCarousel items={relatedIntegrations ?? []} />
            ) : (
              <RelatedList>{relatedIntegrations?.map((item) => item) ?? []}</RelatedList>
            )}

            <RelatedSectionFooter>
              <Link href={INTEGRATIONS_HOME}>
                <a
                  onClick={() => {
                    gtm.track('click', {
                      element_type: 'link',
                      element_label: 'See all integrations',
                      location: 'Other Integrations Section',
                    });
                  }}
                >
                  See all integrations
                </a>
              </Link>
            </RelatedSectionFooter>
          </RelatedSection>
        )}

        <CTABlock>
          <p>Start your free trial today</p>
          <div>
            <FrontPinkButton
              onClick={() => {
                toggleGetStartedModal(true);

                gtm.track('click', {
                  element_type: 'button',
                  element_label: 'Get Started',
                  location: 'CTA Block',
                });
              }}
            >
              Get started
            </FrontPinkButton>
          </div>
        </CTABlock>
      </Container>
    </>
  );
};

Integration.template = MasterTemplate;

export default Integration;

export const getStaticPaths = getStaticPagePaths('integrations', async (context) => {

  const client = new CraftClient();

  const data = await client.request<IntegrationPathsQuery>(IntegrationPathsQueryDocument);

  const paths =
    data.entries?.map((path) => ({
      params: {
        slug: path?.slug!,
      },
    })) || [];

  return {
    paths,
    fallback: 'blocking',
  };
});

export const getStaticProps = getStaticPageProps<{}, { slug: string }>(
  (params) => `integrations/${params?.slug}`,
  async ({ client, context: { params } }) => {
    const slug = params?.slug!;

    const logger = createLogger('pages/integrations/[slug].tsx');

    logger.info(`Building integration: ${slug}`);

    try {

      const pageProps = await getPageProps<IntegrationQuery, IntegrationQueryVariables>({
        pathPrefix: '/integrations',
        queryDocument: IntegrationQueryDocument,
        client,
        variables: {
          slug,
        },
      });

      if (!pageProps) return { notFound: true };

      // Fetch related links for the intergrations page - 
      const graphiteRelatedLinks = await fetchRelatedLinks({
        pathPrefix: 'integrations',
        slug
      });

      return {
        props: {
          entry: pageProps.entry,
          graphiteRelatedLinks,
        },
      };
    } catch (error) {
      logger.error(`Error building integration ${slug}:`, error);

      /*
       * In preview environments, if there's an error, return notFound
       * This prevents the build from failing completely
       */
      return { notFound: true };
    }
  },
);
